import { Plus } from 'lucide-react'

import { BLOCKS_PER_SLIDE_CAP } from '../constants'
import type { BlockView } from '../hooks/useResolvedBlocks'
import { REGION_LABELS } from '../layouts'
import type { Slide } from '../types/presentation.types'
import { SlideRenderer } from './SlideRenderer'
import { SlideStage } from './SlideStage'

/**
 * The slide being edited, full size and scaled to fit (MYD-495).
 *
 * All drawing is `SlideRenderer`'s; this only supplies the editor's
 * interaction: click a block to select it, click empty space to select the
 * slide, and "Add element" in any region with room. Sizes inside the stage
 * are logical pixels, so the buttons are drawn large and shrink with it.
 */
interface SlideCanvasProps {
	slide: Slide
	blocks: Record<string, BlockView | undefined>
	theme?: string
	selectedBlockId: string | null
	onSelectBlock: (blockId: string | null) => void
	onAddElement: (region: string) => void
}

export function SlideCanvas({ slide, blocks, theme, selectedBlockId, onSelectBlock, onAddElement }: SlideCanvasProps) {
	const full = slide.blocks.length >= BLOCKS_PER_SLIDE_CAP

	return (
		// data-slide-canvas: the inspector measures a block here, and only here,
		// since the sorter's thumbnails carry the same block ids (MYD-500).
		<div className="flex h-full w-full items-center justify-center" onClick={() => onSelectBlock(null)} data-slide-canvas>
			<SlideStage className="drop-shadow-md">
				<SlideRenderer
					slide={slide}
					blocks={blocks}
					theme={theme}
					interaction={{
						selectedBlockId,
						onSelectBlock,
						renderEmptyRegion: region => (
							<button
								type="button"
								disabled={full}
								onClick={event => {
									event.stopPropagation()
									onAddElement(region)
								}}
								className="flex min-h-[140px] flex-1 flex-col items-center justify-center gap-3 rounded-xl border-[3px] border-dashed border-gray-300 text-[24px] font-medium text-gray-400 transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary disabled:pointer-events-none disabled:opacity-40"
							>
								<Plus className="h-10 w-10" />
								Add element
								<span className="text-[18px] font-normal">{REGION_LABELS[region] ?? region}</span>
							</button>
						),
						renderRegionFooter: region =>
							full ? null : (
								<button
									type="button"
									onClick={event => {
										event.stopPropagation()
										onAddElement(region)
									}}
									className="flex h-12 shrink-0 items-center justify-center gap-2 rounded-lg border-2 border-dashed border-transparent text-[18px] text-gray-300 transition-colors hover:border-gray-300 hover:text-gray-500"
									aria-label={`Add another element to ${REGION_LABELS[region] ?? region}`}
								>
									<Plus className="h-6 w-6" />
								</button>
							),
					}}
				/>
			</SlideStage>
		</div>
	)
}

export default SlideCanvas
